import React from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';

function CookiePolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-primary hover:text-primary-hover mb-6 transition-colors"
        >
          <Icon icon="fluent:arrow-left-24-regular" width="20" />
          <span className="font-medium">Back</span>
        </button>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Cookie Policy</h1>
            <p className="text-sm text-gray-500">Last updated: {new Date().toLocaleDateString()}</p>
          </div>

          <div className="space-y-6 text-gray-700">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">What Are Cookies?</h2>
              <p className="leading-relaxed">
                Cookies are small text files stored on your device when you visit our platform. They help us provide you with a better experience by remembering your preferences and understanding how you use our services.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Types of Cookies We Use</h2>
              
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Icon icon="fluent:shield-checkmark-24-regular" width="20" className="text-blue-600" />
                    Essential Cookies
                  </h3>
                  <p className="text-sm leading-relaxed">
                    Required for the platform to function properly. These cookies enable core functionality such as security, authentication, and session management. They cannot be disabled.
                  </p>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Icon icon="fluent:chart-multiple-24-regular" width="20" className="text-green-600" />
                    Analytics Cookies
                  </h3>
                  <p className="text-sm leading-relaxed">
                    Help us understand how visitors interact with our platform by collecting and reporting information anonymously. This helps us improve our services and user experience.
                  </p>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Icon icon="fluent:settings-24-regular" width="20" className="text-purple-600" />
                    Functional Cookies
                  </h3>
                  <p className="text-sm leading-relaxed">
                    Enable enhanced functionality and personalization, such as remembering your preferences, language settings, and customized content display.
                  </p>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Icon icon="fluent:target-24-regular" width="20" className="text-orange-600" />
                    Performance Cookies
                  </h3>
                  <p className="text-sm leading-relaxed">
                    Collect information about how you use our platform to help us improve performance, identify errors, and optimize loading times.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">How We Use Cookies</h2>
              <p className="leading-relaxed mb-2">We use cookies to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Keep you signed in during your session</li>
                <li>Remember your preferences and settings</li>
                <li>Understand how you navigate through our platform</li>
                <li>Improve platform performance and user experience</li>
                <li>Provide personalized content and recommendations</li>
                <li>Ensure security and prevent fraudulent activity</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Managing Cookies</h2>
              <p className="leading-relaxed mb-3">
                You can control and manage cookies in various ways. Please note that removing or blocking cookies may impact your user experience and some features may not function properly.
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Browser Settings</h3>
                <p className="text-sm leading-relaxed mb-2">
                  Most browsers allow you to control cookies through their settings. You can:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-sm">
                  <li>View and delete cookies</li>
                  <li>Block third-party cookies</li>
                  <li>Block cookies from specific sites</li>
                  <li>Block all cookies</li>
                  <li>Delete all cookies when closing the browser</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Third-Party Cookies</h2>
              <p className="leading-relaxed">
                We may use third-party services that set cookies on our platform for analytics and performance monitoring. These third parties have their own privacy policies and cookie policies.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Updates to This Policy</h2>
              <p className="leading-relaxed">
                We may update this Cookie Policy from time to time to reflect changes in technology, legislation, or our practices. Please review this page periodically for the latest information.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Contact Us</h2>
              <p className="leading-relaxed">
                If you have questions about our use of cookies, please contact us through the Help section or reach out to your organization administrator.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CookiePolicy;
